/**
 * Column + reference-sheet spec for the dynamically generated purchase-import
 * Excel template. Pure data (no React, no exceljs) so it unit-tests directly
 * and the builder in {@link ./excelTemplate} can stay a thin renderer over it.
 *
 * The columns mirror the Manual-Import product-line field set (the page that
 * doubles as the Excel-import confirmation screen). Branch, invoice number and
 * date are deliberately absent — they're chosen on that confirmation page, not
 * per product row. Variation axes are deferred.
 *
 * Dropdown columns are validated against either a DB-backed reference sheet
 * (suppliers / categories / brands) or a static enum (units / VAT). DB columns
 * carry the entity **name**; the future importer resolves name → id.
 */

export type TemplateColumnKind = 'text' | 'number' | 'date' | 'dropdown';

/** Where a dropdown column draws its allowed values from. */
export type TemplateDropdownSource =
    'suppliers' | 'categories' | 'brands' | 'units' | 'vat';

/** Stable slugs of the template's columns, in no particular order. */
export type TemplateColumnKey =
    | 'supplier'
    | 'barcode'
    | 'sku'
    | 'name'
    | 'category'
    | 'brand'
    | 'unit'
    | 'vat'
    | 'quantity'
    | 'purchasePrice'
    | 'salePrice'
    | 'wholesalePrice'
    | 'minimumQuantity'
    | 'expiryDate'
    | 'saleDueDate';

export type TemplateColumn = {
    /** Stable slug — used for the label lookup and as the test handle. */
    key: TemplateColumnKey;
    /** i18n key (relative to the `pages` namespace) for the header cell. */
    labelKey: string;
    kind: TemplateColumnKind;
    /** Whether the field is mandatory in Manual Import (drives header accent). */
    required: boolean;
    /** Present iff `kind === 'dropdown'`. */
    source?: TemplateDropdownSource;
    /** Approximate column width in Excel character units. */
    width: number;
};

/**
 * Ordered template columns. Order = the order users scan the physical invoice:
 * who it's from, what the product is, how it's classified, then the numerics
 * and dates.
 */
export const TEMPLATE_COLUMNS: readonly TemplateColumn[] = [
    {
        key: 'supplier',
        labelKey: 'purchases.manualImport.supplier',
        kind: 'dropdown',
        source: 'suppliers',
        required: true,
        width: 24,
    },
    {
        key: 'barcode',
        labelKey: 'purchases.manualImport.barcode',
        kind: 'text',
        required: true,
        width: 18,
    },
    {
        key: 'sku',
        labelKey: 'purchases.manualImport.sku',
        kind: 'text',
        required: true,
        width: 16,
    },
    {
        key: 'name',
        labelKey: 'purchases.manualImport.productName',
        kind: 'text',
        required: true,
        width: 28,
    },
    {
        key: 'category',
        labelKey: 'purchases.manualImport.category',
        kind: 'dropdown',
        source: 'categories',
        required: false,
        width: 22,
    },
    {
        key: 'brand',
        labelKey: 'purchases.manualImport.brand',
        kind: 'dropdown',
        source: 'brands',
        required: false,
        width: 20,
    },
    {
        key: 'unit',
        labelKey: 'purchases.manualImport.unit',
        kind: 'dropdown',
        source: 'units',
        required: true,
        width: 14,
    },
    {
        key: 'vat',
        labelKey: 'purchases.manualImport.vat',
        kind: 'dropdown',
        source: 'vat',
        required: true,
        width: 14,
    },
    {
        key: 'quantity',
        labelKey: 'purchases.manualImport.quantity',
        kind: 'number',
        required: true,
        width: 12,
    },
    {
        key: 'purchasePrice',
        labelKey: 'purchases.manualImport.purchasePrice',
        kind: 'number',
        required: true,
        width: 14,
    },
    {
        key: 'salePrice',
        labelKey: 'purchases.manualImport.salePrice',
        kind: 'number',
        required: true,
        width: 14,
    },
    {
        key: 'wholesalePrice',
        labelKey: 'purchases.manualImport.wholesalePrice',
        kind: 'number',
        required: false,
        width: 16,
    },
    {
        key: 'minimumQuantity',
        labelKey: 'purchases.manualImport.minimumQuantity',
        kind: 'number',
        required: false,
        width: 16,
    },
    {
        key: 'expiryDate',
        labelKey: 'purchases.manualImport.expiryDate',
        kind: 'date',
        required: false,
        width: 18,
    },
    {
        key: 'saleDueDate',
        labelKey: 'purchases.manualImport.saleDueDate',
        kind: 'date',
        required: false,
        width: 18,
    },
] as const;

/** DB-backed reference sheets — one per dynamic dropdown source. */
export type ReferenceSheetKey = 'suppliers' | 'categories' | 'brands';

export type ReferenceSheetSpec = {
    key: ReferenceSheetKey;
    /** Workbook-scoped defined name the main sheet's validation points at. */
    definedName: string;
};

export const REFERENCE_SHEETS: readonly ReferenceSheetSpec[] = [
    { key: 'suppliers', definedName: 'SupplierList' },
    { key: 'categories', definedName: 'CategoryList' },
    { key: 'brands', definedName: 'BrandList' },
] as const;

/** The DB values that fill the reference sheets (names only for v1). */
export type PurchaseTemplateData = Record<ReferenceSheetKey, string[]>;

/** Localized strings the builder needs — resolved by the caller via i18next. */
export type PurchaseTemplateLabels = {
    /** column key → header text */
    columns: Record<string, string>;
    /** sheet display names */
    sheets: {
        products: string;
        suppliers: string;
        categories: string;
        brands: string;
    };
};

/** Number of body rows the dropdown validations are applied to. */
export const TEMPLATE_BODY_ROWS = 500;
